import { useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { UUID } from '@/types/common';
import { WorkOrderMaterial } from '@/types/workOrders';
import { useToast } from '@/hooks/useToast';

/**
 * Find or create an inventory item for a BOM component.
 * This ensures proper linkage between design (BOM) and inventory systems.
 * 
 * @param bomComponent - The BOM component to find/create inventory item for
 * @returns The inventory item ID or null if creation fails
 */
async function findOrCreateInventoryItem(bomComponent: {
  id: string;
  component_name: string;
  drawing_number?: string;
  material?: string;
  raw_material_size?: string;
  unit?: string;
}): Promise<string | null> {
  try {
    // First, check if an inventory item already exists for this BOM component
    const { data: existingItem, error: findError } = await supabase
      .from('inventory_items')
      .select('id')
      .eq('bom_component_id', bomComponent.id)
      .single();

    if (existingItem && !findError) {
      return existingItem.id;
    }

    // If not found by bom_component_id, try to find by drawing_number (code)
    if (bomComponent.drawing_number) {
      const { data: itemByCode, error: codeError } = await supabase
        .from('inventory_items')
        .select('id')
        .eq('code', bomComponent.drawing_number)
        .single();

      if (itemByCode && !codeError) {
        // Update the existing item to link it to this BOM component
        await supabase
          .from('inventory_items')
          .update({ bom_component_id: bomComponent.id })
          .eq('id', itemByCode.id);
        return itemByCode.id;
      }
    }

    // No existing item found - create a new inventory item
    // Generate a unique code based on drawing number or component name
    const itemCode = bomComponent.drawing_number || 
      `RM-${bomComponent.component_name.substring(0, 20).replace(/\s+/g, '-').toUpperCase()}-${Date.now().toString(36).toUpperCase()}`;

    // Build description from available fields
    let description = bomComponent.component_name;
    if (bomComponent.material) {
      description += ` | Material: ${bomComponent.material}`;
    }
    if (bomComponent.raw_material_size) {
      description += ` | Size: ${bomComponent.raw_material_size}`;
    }

    // Get the current user
    const { data: { user } } = await supabase.auth.getUser();

    // Get default warehouse (Main Warehouse)
    const { data: defaultWarehouse } = await supabase
      .from('warehouse')
      .select('id')
      .eq('is_default', true)
      .single();

    // Get or create a default UOM
    const { data: defaultUom } = await supabase
      .from('uoms')
      .select('id')
      .eq('code', bomComponent.unit || 'NOS')
      .single();

    const { data: newItem, error: createError } = await supabase
      .from('inventory_items')
      .insert({
        code: itemCode,
        name: bomComponent.component_name,
        description: description,
        category: 'Raw Materials',
        item_type: 'raw_material',
        make_or_buy: 'buy',
        unit: bomComponent.unit || 'NOS',
        uom_id: defaultUom?.id,
        stock_level: 0,
        reorder_level: 0,
        min_stock_level: 0,
        max_stock_level: 0,
        default_warehouse_id: defaultWarehouse?.id,
        bom_component_id: bomComponent.id,
        is_active: true,
        created_by: user?.id,
        specifications: {
          material: bomComponent.material,
          raw_material_size: bomComponent.raw_material_size,
          source: 'auto_created_from_bom'
        }
      })
      .select('id')
      .single();

    if (createError) {
      console.error('Error creating inventory item for BOM component:', createError);
      return null;
    }

    // Create initial warehouse_stock record with 0 quantity
    if (newItem && defaultWarehouse) {
      await supabase
        .from('warehouse_stock')
        .insert({
          item_id: newItem.id,
          warehouse_id: defaultWarehouse.id,
          quantity: 0,
          reserved_quantity: 0,
          available_quantity: 0
        });
    }

    console.log(`Created new inventory item ${itemCode} for BOM component ${bomComponent.component_name}`);
    return newItem?.id || null;
  } catch (error) {
    console.error('Error in findOrCreateInventoryItem:', error);
    return null;
  }
}

/**
 * Get available stock for an inventory item across all warehouses
 */
async function getAvailableStock(inventoryItemId: string): Promise<number> {
  try {
    const { data, error } = await supabase
      .from('warehouse_stock')
      .select('available_quantity')
      .eq('item_id', inventoryItemId);

    if (error || !data) return 0;

    return data.reduce((sum, ws) => sum + (parseFloat(ws.available_quantity) || 0), 0);
  } catch {
    return 0;
  }
}

export const useCreateWorkOrderMaterials = () => {
  const { toast } = useToast();

  const createWorkOrderMaterials = useCallback(async (
    workOrderId: UUID,
    bomId: UUID,
    assemblyId: UUID,
    workOrderQuantity: number,
    selectedComponentIds?: UUID[]
  ): Promise<WorkOrderMaterial[] | null> => {
    try {
      console.log('Creating work order materials for Work Order:', workOrderId, 'BOM:', bomId, 'Assembly:', assemblyId);

      // Fetch all BOM components for the BOM with full details
      const { data: allBomComponents, error: fetchError } = await supabase
        .from('bom_components')
        .select('*')
        .eq('bom_id', bomId);

      if (fetchError) {
        console.error('Error fetching BOM components:', fetchError);
        throw fetchError;
      }

      // Find the assembly component
      const assemblyComponent = allBomComponents?.find(component => component.id === assemblyId);

      if (!assemblyComponent) {
        console.warn('Assembly component not found:', assemblyId);
        return [];
      }

      // Get all components in the assembly hierarchy
      const getAssemblyHierarchy = (components: any[], parentId: string): any[] => {
        const children = components.filter(component => component.parent_component_id === parentId);
        let result = [...children];

        for (const child of children) {
          result = result.concat(getAssemblyHierarchy(components, child.id));
        }

        return result;
      };

      const assemblyHierarchy = getAssemblyHierarchy(allBomComponents || [], assemblyId);

      // Filter to get only raw material components
      let bomComponents = assemblyHierarchy.filter(component => component.component_type === 'raw_material');

      // Further filter by selected components if provided
      if (selectedComponentIds && selectedComponentIds.length > 0) {
        bomComponents = bomComponents.filter(component =>
          selectedComponentIds.includes(component.id) ||
          selectedComponentIds.includes(component.parent_component_id)
        );
      }

      if (!bomComponents || bomComponents.length === 0) {
        console.warn('No raw material BOM components found for assembly:', assemblyId);
        return [];
      }

      console.log(`Processing ${bomComponents.length} raw material components for inventory linkage`);

      // Process each BOM component to find or create inventory items
      const workOrderMaterials = [];

      for (const component of bomComponents) {
        // Find or create inventory item for this BOM component
        const inventoryItemId = await findOrCreateInventoryItem({
          id: component.id,
          component_name: component.component_name,
          drawing_number: component.drawing_number,
          material: component.material,
          raw_material_size: component.raw_material_size,
          unit: component.unit
        });

        // Get available stock for this item
        const availableQty = inventoryItemId ? await getAvailableStock(inventoryItemId) : 0;

        workOrderMaterials.push({
          work_order_id: workOrderId,
          bom_component_id: component.id,
          material_id: inventoryItemId, // Link to inventory_items
          required_quantity: component.quantity * workOrderQuantity,
          available_quantity: availableQty,
          issued_quantity: 0,
          returned_quantity: 0,
          status: 'Not Issued',
          warehouse_id: null, // Will be set when materials are issued
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      }

      console.log('Work order materials to insert:', workOrderMaterials.length);

      // Insert work order materials
      const { data, error } = await supabase
        .from('work_order_materials')
        .insert(workOrderMaterials)
        .select();

      if (error) {
        console.error('Error creating work order materials:', error);
        throw error;
      }

      console.log('Successfully created work order materials with inventory linkage:', data?.length || 0);

      return data;
    } catch (error: any) {
      console.error('Error in createWorkOrderMaterials:', error);
      toast({
        title: 'Error creating work order materials',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    }
  }, [toast]);

  return { createWorkOrderMaterials };
};
